// frontend/src/lib/domains/trust.ts
import { apiGet, apiPost } from "../core/request";

/** ---------- Types ---------- */
export type TrustEntity = {
  id: number;
  user_id: number;
  trust_name: string;
  enforcer: string | null;
  enforcer_address?: string | null;
  protector_name?: string | null;
  protector_address?: string | null;
  settlor_name?: string | null;
  settlor_address?: string | null;
  settlor_city?: string | null;
  settlor_postal_code?: string | null;
  settlor_country?: string | null;
  jurisdiction?: string | null; // Human label, e.g. "The Bahamas" / "United Kingdom" / "Wyoming (USA)"
  jurisdiction_code?: string | null; // Machine code: "bahamas" | "wyoming" | "uk"
  status: "draft" | "pending" | "active" | "rejected" | "terminated";
  rejection_reason?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type TrustAccount = {
  id: number;
  trust_entity_id: number;
  currency: string;
  fiat_balance: number | string;
  status: string;
  created_at?: string;
  updated_at?: string;
};

export type TrustWish = {
  id: number;
  user_id: number;
  trust_id: number;
  trust_account_id: number;
  type: "buy" | "sell" | "withdraw";
  from_asset: string | null;
  to_asset: string | null;
  amount: number;
  rate_used: number;
  final_amount: number;
  status: "pending" | "approved" | "rejected" | "executed" | "cancelled";
  description?: string | null;
  network?: string | null;
  dest_address?: string | null;
  dest_memo?: string | null;
  otp_verified_at?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type EligibilityResponse = {
  ok: boolean;
  action: "apply" | "not-eligible" | "reapply" | "dashboard" | "blocked";
  eligibility: {
    eligible: boolean;
    minDeposit: number;
    userDeposited: number;
    remaining: number;
    latestStatus: string | null;
    latestTrustId: number | null;
  };
  trust_id?: number;
  message?: string;
  error?: string;
};

export type QuoteParams = {
  type: "buy" | "sell";
  asset: "USDT" | "USDC" | "BTC" | "ETH" | string;
  amount: number | string;
};

export type QuoteResponse = {
  ok: boolean;
  type?: "buy" | "sell";
  asset?: string;
  amount_input?: number;
  rate_used?: number;
  fee_percent?: number;
  final_amount?: number;
  error?: string;
};

/** ---------- API ---------- */

export async function apiTrustEligibility(): Promise<EligibilityResponse> {
  return apiGet("/xapi/trusts/eligibility");
}

/**
 * First-time SPA application:
 * - Mirrors web: trust_name, enforcer, enforcer_address, jurisdiction (bahamas|wyoming|uk), terms.
 */
export async function apiTrustApply(payload: {
  trust_name: string;
  enforcer: string;
  enforcer_address: string;
  jurisdiction: "bahamas" | "wyoming" | "uk";
  terms: boolean;
}): Promise<{
  ok: boolean;
  trust_id?: number;
  status?: string;
  error?: string;
  requires_reapply?: boolean;
}> {
  return apiPost("/xapi/trusts/apply", payload);
}

/**
 * Re-apply against an existing rejected TrustEntity.
 */
export async function apiTrustReapply(
  trustId: number,
  payload: {
    trust_name: string;
    enforcer: string;
    enforcer_address: string;
    jurisdiction: "bahamas" | "wyoming" | "uk";
    terms: boolean;
  }
) {
  return apiPost(`/xapi/trusts/${trustId}/reapply`, payload);
}

export async function apiTrustsIndex(): Promise<{ ok: boolean; trusts: TrustEntity[] }> {
  return apiGet("/xapi/trusts");
}

export async function apiTrustShow(id: number): Promise<{
  ok: boolean;
  trust: TrustEntity;
  accounts: TrustAccount[];
  eurBalance: number;
  eurAvailable: number;
  pendingEur: number;
  cryptoCodes: string[];
  cryptoBalances: Record<string, number>;
  cryptoLocked: Record<string, number>;
  cryptoAvailable: Record<string, number>;
  wishes: TrustWish[];
}> {
  return apiGet(`/xapi/trusts/${id}`);
}

/**
 * Live quote for Trust wish (EUR <-> CRYPTO), used by SPA to show
 * "Final (after hidden 5%)" before submitting the wish.
 */
export async function apiTrustQuote(
  trustId: number | string,
  params: QuoteParams
): Promise<QuoteResponse> {
  const q = new URLSearchParams({
    type: String(params.type),
    asset: String(params.asset),
    amount: String(params.amount),
  });
  return apiGet(`/xapi/trusts/${trustId}/quote?` + q.toString());
}

/**
 * Robust PDF preview fetcher for the Trust deed.
 *
 * Flow (mirrors web flow but via XAPI):
 *  1) POST /xapi/trusts/deed/preview  → { ok, preview_url }
 *  2) GET  preview_url (signed /trust/deed/preview?...) → PDF Blob
 *  3) Return { blob, filename? } so callers can open it.
 */
export async function apiTrustDeedPreview(payload: {
  trust_name: string;
  enforcer: string;
  enforcer_address?: string;
  jurisdiction: "bahamas" | "wyoming" | "uk";
}): Promise<{ blob: Blob; filename?: string }> {
  // Step 1: ask XAPI for a signed preview URL (use apiPost so CSRF is handled)
  const json = await apiPost<{
    ok: boolean;
    preview_url?: string;
    message?: string;
    error?: string;
  }>("/xapi/trusts/deed/preview", payload);

  if (!json?.ok || !json.preview_url) {
    throw new Error(json?.message || json?.error || "Failed to generate deed preview.");
  }

  const previewUrl = json.preview_url;

  // Step 2: fetch the actual PDF from the signed /trust/deed/preview URL
  const pdfRes = await fetch(previewUrl, {
    method: "GET",
    credentials: "include",
    headers: {
      Accept: "application/pdf",
      "X-Requested-With": "XMLHttpRequest",
    },
  });

  if (!pdfRes.ok) {
    throw new Error("Failed to download deed PDF.");
  }

  const blob = await pdfRes.blob();

  // Best-effort filename from Content-Disposition (if any)
  const cd = pdfRes.headers.get("content-disposition") || "";
  const match = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(cd);
  const filename = decodeURIComponent(match?.[1] || match?.[2] || "");

  return { blob, filename: filename || undefined };
}

/** Helper to open the returned PDF safely in a new tab (works around popup blockers a bit). */
export function openPdfBlob({ blob, filename }: { blob: Blob; filename?: string }) {
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "noopener");
  if (!w) {
    // Fallback to download if popup blocked
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "trust-deed.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Optional: revoke later; keeping it for tab-lifetime is fine
  // setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Commit the deed (user clicks "I accept" after preview).
 * Mirrors web: includes jurisdiction + enforcer_address.
 */
export async function apiTrustDeedCommit(payload: {
  trust_name: string;
  enforcer: string;
  enforcer_address: string;
  jurisdiction: "bahamas" | "wyoming" | "uk";
  accept_deed: boolean;
}): Promise<{ ok: boolean; trust_id?: number; status?: string }> {
  return apiPost("/xapi/trusts/deed/commit", payload);
}

export async function apiTrustTransferEUR(payload: { amount: number }) {
  return apiPost("/xapi/trusts/transfer", payload);
}

export async function apiTrustCreateWish(
  trustId: number,
  payload: {
    type: "buy" | "sell";
    asset: "USDT" | "USDC" | "BTC" | "ETH";
    amount: number;
    description: string;
    rate_used?: number;
    final_amount?: number;
  }
): Promise<{ ok: boolean; wish_id?: number; error?: string }> {
  return apiPost(`/xapi/trusts/${trustId}/wishes`, payload);
}

export async function apiTrustCreateWithdraw(
  trustId: number,
  payload: {
    asset: "USDT" | "USDC" | "BTC" | "ETH";
    network: string;
    amount: number;
    dest_address: string;
    dest_memo?: string;
    description?: string;
  }
): Promise<{ ok: boolean; wish_id?: number }> {
  return apiPost(`/xapi/trusts/${trustId}/withdrawals`, payload);
}

export async function apiTrustWishShow(wishId: number): Promise<{ ok: boolean; wish: TrustWish }> {
  return apiGet(`/xapi/wishes/${wishId}`);
}

export async function apiTrustWishVerify(
  wishId: number,
  payload: { code: string }
): Promise<{ ok: boolean; error?: string }> {
  return apiPost(`/xapi/wishes/${wishId}/verify`, payload);
}

export async function apiTrustWishResendOtp(
  wishId: number
): Promise<{ ok: boolean; message?: string }> {
  return apiPost(`/xapi/wishes/${wishId}/resend-otp`, {});
}
